/**
 * ShareButton - Creates a shareable snapshot of current search results.
 *
 * On click: POSTs current results to /api/export/snapshot,
 * then shows a copy-to-clipboard dialog with the snapshot URL.
 */
import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useCreateSnapshot } from '../hooks/useSnapshot';
import { useTab } from '../shell/TabContext';
import type { DrugResultV2, IntentResponseV2, SearchResultV2, SearchMetadataV2 } from '../types';

interface ShareButtonProps {
  query: string;
  drugResults: DrugResultV2[];
  filters?: Record<string, unknown>;
  intent?: IntentResponseV2;
  evidenceChunks?: SearchResultV2[];
  summary?: string | null;
  metadata?: SearchMetadataV2;
  resultCount?: number;
}

const ShareButton: React.FC<ShareButtonProps> = ({
  query,
  drugResults,
  filters,
  intent,
  evidenceChunks,
  summary,
  metadata,
  resultCount,
}) => {
  const { createSnapshot, isCreating } = useCreateSnapshot();
  // The dialog is portaled to <body>, so hiding this tab's pane does not hide
  // it: a snapshot that finishes after the user switched tabs must not cover
  // the active page. It shows once this tab is visible again.
  const { isActive } = useTab();
  const [showDialog, setShowDialog] = useState(false);
  const [snapshotUrl, setSnapshotUrl] = useState('');
  const [copied, setCopied] = useState(false);
  // See GenerateDeckButton.tsx: a drag-select of the URL that ends outside the
  // panel fires `click` on the nearest common ancestor (the backdrop), which
  // would otherwise dismiss the dialog mid-selection.
  const backdropPressRef = useRef(false);

  const handleShare = async () => {
    try {
      const payload: Parameters<typeof createSnapshot>[0] = {
        query,
        drug_results: drugResults,
      };
      if (filters) payload.filters = filters;
      if (intent) payload.intent = intent;
      if (evidenceChunks) payload.evidence_chunks = evidenceChunks;
      if (summary != null) payload.summary = summary;
      if (metadata) payload.metadata = metadata;
      if (resultCount != null) payload.result_count = resultCount;
      const result = await createSnapshot(payload);
      const fullUrl = `${window.location.origin}/snapshot/${result.share_token}`;
      setSnapshotUrl(fullUrl);
      setShowDialog(true);
      setCopied(false);
    } catch {
      // Error handled by hook
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(snapshotUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for clipboard API failure
      const textarea = document.createElement('textarea');
      textarea.value = snapshotUrl;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <>
      <button
        onClick={handleShare}
        disabled={isCreating || drugResults.length === 0}
        className="btn-secondary"
        title="Save & share these results"
      >
        {isCreating ? (
          <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        ) : (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
          </svg>
        )}
        Share
      </button>

      {/* Copy URL Dialog.
          Portaled to <body> for the same reason as the CEPI export dialog in
          GenerateDeckButton.tsx: an inline `fixed inset-0 z-*` modal only ranks
          within whatever stacking context it happens to render inside. SearchPage
          does not currently nest this under a sticky/transform ancestor and has no
          competing z-indexed layer, so this is hardening, not a live-bug fix — it
          removes the coupling to an ancestor chain this component cannot see. */}
      {showDialog && isActive && createPortal(
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
          onMouseDown={(e) => { backdropPressRef.current = e.target === e.currentTarget; }}
          onClick={(e) => {
            const fromBackdrop = backdropPressRef.current;
            backdropPressRef.current = false;
            if (fromBackdrop && e.target === e.currentTarget) setShowDialog(false);
          }}
        >
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-slate-800 mb-2">Snapshot saved</h3>
            <p className="text-sm text-slate-500 mb-4">Anyone with this link can view these results (read-only).</p>
            <div className="flex gap-2">
              <input
                readOnly
                value={snapshotUrl}
                className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-lg bg-slate-50 text-slate-700"
                onFocus={(e) => e.target.select()}
              />
              <button
                onClick={handleCopy}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  copied
                    ? 'bg-green-100 text-green-700 border border-green-200'
                    : 'bg-indigo-600 text-white hover:bg-indigo-700'
                }`}
              >
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <button
              onClick={() => setShowDialog(false)}
              className="mt-4 w-full text-sm text-slate-500 hover:text-slate-700 transition-colors"
            >
              Close
            </button>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default ShareButton;
